import type { BillingPlan } from '@/features/billing/types'

const PLAN_NAME_MAP: Record<string, string> = {
  free: '免费版',
  trial_3d: '体验会员 3 天',
  trial3d: '体验会员 3 天',
  trial: '体验会员',
  basic: '基础版',
  month: '月会员',
  monthly: '月会员',
  year: '年会员',
  yearly: '年会员',
  lifetime: '永久会员',
  fastcut_free: 'FastCut 免费版',
  fastcut_creator_month: 'FastCut Creator 月付',
  fastcut_creator_year: 'FastCut Creator 年付',
  fastcut_studio_month: 'FastCut Studio 月付',
  fastcut_studio_year: 'FastCut Studio 年付',
}

const FEATURE_LABEL_MAP: Record<string, string> = {
  mcp_access: '支持 MCP 自动化',
}

const PLAN_AI_SECONDS_MAP: Record<string, number> = {
  fastcut_free: 7200,
  fastcut_creator_month: 72000,
  fastcut_creator_year: 72000,
  fastcut_studio_month: 216000,
  fastcut_studio_year: 216000,
}

const PLAN_DESCRIPTION_MAP: Record<string, string> = {
  fastcut_free: '基础剪辑免费，含少量 AI 处理时长',
  fastcut_creator_month: '适合个人创作者，含基础 AI 与 MCP 自动化',
  fastcut_creator_year: '适合长期创作，含基础 AI 与 MCP 自动化',
  fastcut_studio_month: '适合高频生产，含更高 AI 时长与 MCP 自动化',
  fastcut_studio_year: '适合工作室团队，含更高 AI 时长与 MCP 自动化',
}

const PLAN_HIGHLIGHTS_MAP: Record<string, string[]> = {
  fastcut_free: ['基础剪辑免费', '每月 2 小时 AI 处理时长', '可体验基础 AI 能力'],
  fastcut_creator_month: [
    '每月 20 小时 AI 处理时长',
    '支持 MCP 自动化',
    '适合个人创作者',
  ],
  fastcut_creator_year: [
    '每月 20 小时 AI 处理时长',
    '支持 MCP 自动化',
    '适合长期创作',
  ],
  fastcut_studio_month: [
    '每月 60 小时 AI 处理时长',
    '支持 MCP 自动化',
    '更高自动化配额',
  ],
  fastcut_studio_year: [
    '每月 60 小时 AI 处理时长',
    '支持 MCP 自动化',
    '更高自动化配额',
  ],
}

export function formatMembershipPlan(planName?: string | null, planCode?: string | null) {
  const normalizedPlanName = planName?.trim()
  const normalizedPlanCode = planCode?.trim().toLowerCase()
  const localizedByName = normalizedPlanName
    ? PLAN_NAME_MAP[normalizedPlanName.toLowerCase()]
    : undefined

  if (localizedByName) {
    return localizedByName
  }

  if (normalizedPlanCode && PLAN_NAME_MAP[normalizedPlanCode]) {
    return PLAN_NAME_MAP[normalizedPlanCode]
  }

  if (normalizedPlanName) {
    return normalizedPlanName
  }

  return '未开通'
}

export function formatMembershipStatus(status?: string | null) {
  switch (status) {
    case 'active':
      return '已生效'
    case 'trialing':
      return '试用中'
    case 'grace_period':
      return '宽限期'
    case 'expired':
      return '已到期'
    case 'cancelled':
      return '已取消'
    case 'pending':
    case 'pending_activation':
      return '待生效'
    default:
      return '普通用户'
  }
}

export function formatMembershipExpiresAt(value?: string | null, fallback = '暂无') {
  if (!value) {
    return fallback
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

export function hasSubscriptionMembership(planCode?: string | null, expiresAt?: string | null) {
  return Boolean(expiresAt && planCode && planCode !== 'free' && planCode !== 'fastcut_free')
}

export function formatBillingFeature(featureKey: string) {
  return FEATURE_LABEL_MAP[featureKey] ?? featureKey.replace(/_/g, ' ')
}

export function getPlanAiProcessingSeconds(planCode?: string | null) {
  if (!planCode) {
    return null
  }

  return PLAN_AI_SECONDS_MAP[planCode] ?? null
}

export function formatAiHoursLabel(seconds?: number | null) {
  if (!seconds || seconds <= 0) {
    return 'AI 时长待配置'
  }

  const hours = seconds / 3600
  const display = Number.isInteger(hours) ? String(hours) : hours.toFixed(1)
  return `每月 ${display} 小时 AI 处理时长`
}

export function formatRemainingAiHoursLabel(seconds?: number | null) {
  if (seconds == null) {
    return 'AI 时长待同步'
  }

  const hours = seconds / 3600
  const display = hours >= 10 ? Math.floor(hours).toString() : hours.toFixed(1)
  return `剩余 ${display} 小时 AI 处理时长`
}

/**
 * 将 AI 处理时长（秒）格式化为人类可读的时长文案。
 * 小于 1 小时按分钟展示，否则按小时展示（整数不带小数）。
 */
export function formatAiSecondsDuration(seconds?: number | null) {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) {
    return '0 分钟'
  }

  const totalMinutes = Math.round(seconds / 60)
  if (totalMinutes < 60) {
    return `${Math.max(totalMinutes, 1)} 分钟`
  }

  const hours = seconds / 3600
  const display = Number.isInteger(hours) ? String(hours) : hours.toFixed(1)
  return `${display} 小时`
}

/**
 * 根据总额度与剩余额度计算已使用比例（0-100，向下取整避免误显示已满）。
 */
export function calcAiUsagePercent(totalSeconds?: number | null, remainingSeconds?: number | null) {
  if (totalSeconds == null || totalSeconds <= 0 || remainingSeconds == null) {
    return 0
  }

  const used = Math.min(Math.max(totalSeconds - remainingSeconds, 0), totalSeconds)
  return Math.floor((used / totalSeconds) * 100)
}

export function formatBillingCycleLabel(plan: Pick<BillingPlan, 'plan_code' | 'billing_cycle' | 'duration_days'>) {
  if (plan.plan_code === 'fastcut_free') {
    return '免费使用'
  }

  if (plan.billing_cycle === 'yearly') {
    return '年付'
  }

  if (plan.billing_cycle === 'monthly') {
    return '月付'
  }

  return `${plan.duration_days} 天`
}

export function formatBillingPriceLabel(plan: Pick<BillingPlan, 'plan_code' | 'price' | 'billing_cycle'>) {
  if (plan.plan_code === 'fastcut_free') {
    return '0 元'
  }

  return plan.billing_cycle === 'yearly' ? `${plan.price} 元/年` : `${plan.price} 元/月`
}

export function formatBillingSecondaryPrice(plan: Pick<BillingPlan, 'plan_code' | 'price' | 'billing_cycle'>) {
  if (plan.plan_code === 'fastcut_free') {
    return '长期可用'
  }

  if (plan.billing_cycle !== 'yearly') {
    return null
  }

  const yearlyPrice = Number(plan.price)
  if (!Number.isFinite(yearlyPrice) || yearlyPrice <= 0) {
    return null
  }

  return `折合 ${Math.round((yearlyPrice / 12) * 100) / 100} 元/月`
}

export function getLocalizedPlanDescription(plan: BillingPlan) {
  return PLAN_DESCRIPTION_MAP[plan.plan_code] ?? plan.description ?? '适合当前剪辑与 AI 创作场景'
}

export function getLocalizedPlanHighlights(plan: BillingPlan) {
  const mapped = plan.feature_flags
    .map(formatBillingFeature)
    .filter((value, index, array) => array.indexOf(value) === index)

  const aiHours = formatAiHoursLabel(getPlanAiProcessingSeconds(plan.plan_code))
  const highlights = PLAN_HIGHLIGHTS_MAP[plan.plan_code] ?? [aiHours, ...mapped]

  return highlights.filter((value, index, array) => array.indexOf(value) === index)
}

export function localizeBillingPlan(plan: BillingPlan): BillingPlan {
  return {
    ...plan,
    plan_name: formatMembershipPlan(plan.plan_name, plan.plan_code),
    description: getLocalizedPlanDescription(plan),
    feature_flags: getLocalizedPlanHighlights(plan),
  }
}
